import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeLocation, isOneDrive } from '../../data/graph';
import { rememberLastOneDriveFolder } from '../../data/lastOneDriveFolder';
import { type ResolvedDestination, type SaveContext, resolveSaveDestination } from '../../data/saveDestination';
import type { SharePointLocation } from '../../data/types';
import { useGraphConnection } from '../../hooks/useGraphConnection';
import { type ConflictChoice, type SaveItem, useSharePointSave } from '../../hooks/useSharePointSave';
import { LocationPicker } from './LocationPicker';

export type SaveMode = 'default' | 'sharepoint';

interface SaveToSharePointDialogProps {
  context: SaveContext;
  /** `sharepoint` opens on the template's SharePoint choice, or the site list, never on OneDrive. */
  mode: SaveMode;
  /** Called once a document has been saved. */
  onSaved?: () => void;
}

const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60';

const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-1.5 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60';

/** Keeps the original extension when the user types a new name without one. */
function withExtension(name: string, original: string): string {
  const extension = original.includes('.') ? original.slice(original.lastIndexOf('.')) : '';
  const trimmed = name.trim();
  return extension && !trimmed.toLowerCase().endsWith(extension.toLowerCase()) ? `${trimmed}${extension}` : trimmed;
}

function ConflictPrompt({
  item,
  disabled,
  onChoose
}: {
  item: SaveItem;
  disabled: boolean;
  onChoose: (choice: ConflictChoice) => void;
}) {
  const { t } = useTranslation();
  const current = item.saveAs ?? item.fileName;
  const [name, setName] = useState(current);
  const next = withExtension(name, item.fileName);

  return (
    <div className="mt-2 grid gap-2 rounded border border-border bg-muted p-3">
      <p className="text-xs text-foreground">{t('sharePoint.save.conflict', { name: current })}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={disabled} onClick={() => onChoose({ replace: true })} className={secondaryButton}>
          {t('sharePoint.save.replace')}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={t('sharePoint.save.newName')}
          className="min-w-0 flex-1 rounded-sm border border-input bg-surface px-3 py-1.5 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
        />
        <button
          type="button"
          disabled={disabled || next.length === 0 || next === current}
          onClick={() => onChoose({ saveAs: next })}
          className={secondaryButton}
        >
          {t('sharePoint.save.saveAsNew')}
        </button>
      </div>
    </div>
  );
}

function StatusIcon({ item }: { item: SaveItem }) {
  if (item.status === 'saved') return <CheckCircleIcon className="size-4 shrink-0 text-primary" aria-hidden="true" />;
  if (item.status === 'failed') return <ExclamationCircleIcon className="size-4 shrink-0 text-destructive" aria-hidden="true" />;
  if (item.status === 'saving') return <ArrowPathIcon className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />;
  return <span className="size-4 shrink-0" aria-hidden="true" />;
}

/** One location for the document; a failure can be retried and a name clash resolved without starting over. */
function SaveBody({
  context,
  destination,
  user,
  onSaved
}: {
  context: SaveContext;
  destination: ResolvedDestination;
  user: string;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const documents = [{ documentId: context.documentId, fileName: context.fileName }];
  const { items, running, save, resolveConflict, savedCount, failedCount, conflictCount } = useSharePointSave(
    documents,
    (saved) => {
      if (isOneDrive(saved)) rememberLastOneDriveFolder(user, context.templateId, saved);
      onSaved?.();
    }
  );
  const [location, setLocation] = useState<SharePointLocation | null>(destination.location);
  const initialLocation = destination.location;

  const allSaved = savedCount === items.length;

  return (
    <div className="flex flex-col gap-4">
      <ul className="rounded border border-border">
        {items.map((item) => (
          <li key={item.documentId} className="flex items-start gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0">
            <StatusIcon item={item} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-foreground">{item.saveAs ?? item.fileName}</p>
              {item.error ? (
                <p role="alert" className="text-xs text-destructive">
                  {item.error}
                </p>
              ) : null}
              {item.status === 'conflict' && location ? (
                <ConflictPrompt
                  item={item}
                  disabled={running}
                  onChoose={(choice) => void resolveConflict(location, item.documentId, choice)}
                />
              ) : null}
            </div>
            {item.webUrl ? (
              <a
                href={item.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-xs font-semibold text-foreground underline-offset-2 hover:underline"
              >
                {t('sharePoint.save.open')}
              </a>
            ) : null}
          </li>
        ))}
      </ul>

      {allSaved ? (
        <p className="text-sm text-foreground" aria-live="polite">
          {t('sharePoint.save.allSaved', { count: items.length })}
        </p>
      ) : (
        <>
          {destination.notice ? (
            <p className="text-sm text-muted-foreground" role="status">
              {t(destination.notice.key, destination.notice.params)}
            </p>
          ) : null}
          <LocationPicker initialLocation={initialLocation} onChange={setLocation} />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <p className="min-w-0 truncate text-sm text-muted-foreground">
              {location
                ? t('sharePoint.save.target', { location: describeLocation(location) })
                : t('sharePoint.save.chooseLocation')}
            </p>
            <button
              type="button"
              disabled={!location || running || conflictCount > 0}
              onClick={() => location && void save(location)}
              className={primaryButton}
            >
              {running
                ? t('sharePoint.save.saving')
                : failedCount > 0
                  ? t('sharePoint.save.retry')
                  : t('sharePoint.save.action', { count: items.length })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Connects to Microsoft 365 if needed, works out where to open, then shows the picker. */
export function SaveToSharePointDialog({ context, mode, onSaved }: SaveToSharePointDialogProps) {
  const { t } = useTranslation();
  const connection = useGraphConnection();
  const [destination, setDestination] = useState<ResolvedDestination | null>(null);

  const connected = connection.state === 'ready';
  const user = connection.user ?? '';

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;

    // A failed lookup only costs the starting point, never the save.
    resolveSaveDestination(context, mode, user)
      .catch((): ResolvedDestination => ({ location: null, notice: null }))
      .then((resolved) => {
        if (!cancelled) setDestination(resolved);
      });

    return () => {
      cancelled = true;
    };
  }, [connected, context, mode, user]);

  if (connection.state === 'needed') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">{t('sharePoint.picker.connectBody')}</p>
        <button type="button" onClick={connection.connect} className={primaryButton}>
          {t('sharePoint.picker.connect')}
        </button>
        {connection.error ? (
          <p role="alert" className="text-sm text-destructive">
            {connection.error}
          </p>
        ) : null}
      </div>
    );
  }

  if (!destination) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  return <SaveBody context={context} destination={destination} user={user} onSaved={onSaved} />;
}
